import React, { useEffect, useState } from 'react';
import { billingAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  BellAlertIcon,
  DocumentTextIcon,
  ClockIcon,
  ReceiptRefundIcon
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

const Billing = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [phone, setPhone] = useState('');
  const [reference, setReference] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showManualPayment, setShowManualPayment] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await billingAPI.getStatus();
      setStatus(res.data);
      // Pre-fill phone if available
      if (res.data?.user?.phone) {
        setPhone(res.data.user.phone);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submitStk = async (e) => {
    e.preventDefault();
    if (!phone) {
      toast.error('Please enter your phone number');
      return;
    }
    try {
      setProcessing(true);
      await billingAPI.initiateStk(phone);
      toast.success('STK push sent! Complete the payment on your phone.');
      
      // Poll for payment confirmation
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const res = await billingAPI.getStatus();
          if (res.data?.subscription?.status === 'active') {
            clearInterval(pollInterval);
            setStatus(res.data);
            toast.success('Subscription activated successfully!');
            setProcessing(false);
          }
        } catch (e) {
          console.error('Status check error:', e);
        }
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setProcessing(false);
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to initiate STK');
      setProcessing(false);
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!reference) {
      toast.error('Please enter the M-Pesa receipt code');
      return;
    }
    try {
      await billingAPI.submitManual(reference);
      toast.success('Receipt submitted! We will verify and activate your subscription shortly.');
      setReference('');
      setShowManualPayment(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to submit reference');
    }
  };

  const sub = status?.subscription;
  const price = status?.priceKES ?? 1000;

  // Calculate subscription status
  const getSubscriptionStatus = () => {
    if (!sub) return { status: 'none', label: 'No Subscription', color: 'red' };
    
    const endsAt = new Date(sub.endsAt);
    const now = new Date();
    const daysLeft = Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24));
    
    if (sub.status === 'expired' || endsAt <= now) {
      return { status: 'expired', label: 'Expired', color: 'red', daysLeft: 0 };
    }
    if (daysLeft <= 7) {
      return { status: 'expiring', label: `Expires in ${daysLeft} days`, color: 'yellow', daysLeft };
    }
    return { status: 'active', label: 'Active', color: 'green', daysLeft };
  };

  const subStatus = getSubscriptionStatus();
  const isActive = subStatus.status === 'active' || subStatus.status === 'expiring';

  const features = [
    { icon: ChartBarIcon, title: 'Advanced Analytics', desc: 'Track flock performance, production trends, and financials' },
    { icon: CloudArrowUpIcon, title: 'Unlimited Records', desc: 'Store all your farm data securely in the cloud' },
    { icon: BellAlertIcon, title: 'Smart Alerts', desc: 'Get notified about important events and deadlines' },
    { icon: DocumentTextIcon, title: 'Monthly Reports', desc: 'Receive detailed PDF reports via email' },
    { icon: SparklesIcon, title: 'AI Assistant', desc: 'Get expert advice and diagnose issues with AI' },
    { icon: ShieldCheckIcon, title: 'Priority Support', desc: 'Get help when you need it most' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-emerald-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <CreditCardIcon className="h-8 w-8" />
            <h1 className="text-2xl sm:text-3xl font-bold">Subscription & Billing</h1>
          </div>
          <p className="text-primary-100 text-sm sm:text-base max-w-xl">
            Unlock the full potential of Omabra to manage your poultry farm efficiently.
          </p>
        </div>
      </div>

      {/* Current Status Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className={`p-6 ${
          subStatus.color === 'green' ? 'bg-gradient-to-r from-green-50 to-emerald-50' :
          subStatus.color === 'yellow' ? 'bg-gradient-to-r from-yellow-50 to-amber-50' :
          'bg-gradient-to-r from-red-50 to-orange-50'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${
                subStatus.color === 'green' ? 'bg-green-100' :
                subStatus.color === 'yellow' ? 'bg-yellow-100' :
                'bg-red-100'
              }`}>
                {subStatus.color === 'green' ? (
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
                ) : subStatus.color === 'yellow' ? (
                  <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
                ) : (
                  <XCircleIcon className="h-8 w-8 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Current Status</p>
                <p className={`text-xl font-bold ${
                  subStatus.color === 'green' ? 'text-green-700' :
                  subStatus.color === 'yellow' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  {subStatus.label}
                </p>
                {sub?.endsAt && (
                  <p className="text-sm text-gray-500 mt-1">
                    {isActive ? 'Valid until' : 'Expired on'}: {new Date(sub.endsAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                )}
              </div>
            </div>
            
            {isActive && subStatus.daysLeft <= 30 && (
              <div className="text-right">
                <div className="inline-flex items-center px-4 py-2 bg-white rounded-lg shadow-sm border">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">
                    <span className="font-bold text-gray-900">{subStatus.daysLeft}</span> days remaining
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Progress bar for active subscriptions */}
        {isActive && sub?.startsAt && sub?.endsAt && (
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>Started: {new Date(sub.startsAt).toLocaleDateString()}</span>
              <span>Ends: {new Date(sub.endsAt).toLocaleDateString()}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  subStatus.color === 'green' ? 'bg-green-500' :
                  subStatus.color === 'yellow' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ 
                  width: `${Math.max(0, Math.min(100, ((new Date() - new Date(sub.startsAt)) / (new Date(sub.endsAt) - new Date(sub.startsAt))) * 100))}%` 
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Pricing & Payment Section */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Plan Details */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900">Monthly Plan</h2>
            <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
              Popular
            </span>
          </div>
          
          <div className="mb-6">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-gray-900">KES {price.toLocaleString()}</span>
              <span className="text-gray-500">/month</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Billed monthly via M-Pesa</p>
          </div>

          <div className="space-y-3">
            {features.map((feature, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="p-1 bg-green-100 rounded-full mt-0.5">
                  <CheckIcon className="h-3 w-3 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{feature.title}</p>
                  <p className="text-xs text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Form */}
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            <DevicePhoneMobileIcon className="h-6 w-6 mr-2 text-green-600" />
            {isActive ? 'Renew Subscription' : 'Activate Subscription'}
          </h2>

          {/* M-Pesa Payment */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/M-PESA_LOGO-01.svg/1200px-M-PESA_LOGO-01.svg.png" 
                alt="M-Pesa" 
                className="h-8 object-contain"
              />
              <div>
                <p className="font-medium text-gray-900">Pay with M-Pesa</p>
                <p className="text-xs text-gray-500">Instant activation via STK Push</p>
              </div>
            </div>
            
            <form onSubmit={submitStk} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  M-Pesa Phone Number
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">+254</span>
                  <input
                    type="tel"
                    value={phone.replace(/^254/, '').replace(/^\+254/, '')}
                    onChange={(e) => setPhone('254' + e.target.value.replace(/\D/g, '').slice(0, 9))}
                    className="w-full pl-14 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="7XXXXXXXX"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the number registered with M-Pesa
                </p>
              </div>
              
              <button
                type="submit"
                disabled={processing}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Waiting for payment...
                  </>
                ) : (
                  <>
                    <CreditCardIcon className="h-5 w-5" />
                    Pay KES {price.toLocaleString()} Now
                  </>
                )}
              </button>
            </form>
            
            {processing && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Check your phone for the M-Pesa prompt and enter your PIN to complete the payment.
                </p>
              </div>
            )}
          </div>

          {/* Manual Payment Toggle */}
          <div className="border-t border-gray-200 pt-6">
            <button
              onClick={() => setShowManualPayment(!showManualPayment)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <ReceiptRefundIcon className="h-5 w-5" />
              {showManualPayment ? 'Hide' : 'Already paid? Enter receipt manually'}
            </button>
            
            {showManualPayment && (
              <form onSubmit={submitManual} className="mt-4 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  M-Pesa Receipt Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reference}
                    onChange={(e) => setReference(e.target.value.toUpperCase())}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                    placeholder="e.g., QBC123XXXX"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Submit
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Enter the M-Pesa confirmation code you received after paying to Paybill 4107197
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 sm:p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
          Everything you need to manage your poultry farm
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, i) => (
            <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <feature.icon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{feature.title}</p>
                  <p className="text-xs text-gray-500">{feature.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
        <h3 className="font-semibold text-gray-900 mb-2">Need help?</h3>
        <p className="text-sm text-gray-500 mb-4">
          Contact us via WhatsApp or email for payment assistance
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href="https://wa.me/254114428944"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
          >
            WhatsApp Support
          </a>
          <a
            href="mailto:support@omabra.com"
            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Email Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default Billing;
